import React, { useEffect, useState, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import LandingPage from './LandingPage';
import AuthPage from './AuthPage';
import OnboardingPage from './OnboardingPage';
import WaitingPage from './WaitingPage';
import DashboardPage from './DashboardPage';
import DisabledPage from './DisabledPage';
import { useLanguage } from '../i18n';

async function loadActiveVeyl(userId) {
  const veylsRef = collection(db, 'veyls');
  const matchedQ = query(
    veylsRef,
    where('userId', '==', userId),
    where('status', 'in', ['matched', 'complete']),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  const matchedSnap = await getDocs(matchedQ);
  if (!matchedSnap.empty) {
    const veylDoc = matchedSnap.docs[0];
    return { id: veylDoc.id, ...veylDoc.data() };
  }
  const seekingQ = query(
    veylsRef,
    where('userId', '==', userId),
    where('status', '==', 'seeking_partner'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  const seekingSnap = await getDocs(seekingQ);
  if (!seekingSnap.empty) {
    const veylDoc = seekingSnap.docs[0];
    return { id: veylDoc.id, ...veylDoc.data() };
  }
  return null;
}

function isFromPreviousDay(timestamp) {
  if (!timestamp) return true;
  const now = new Date();
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return (
    d.getDate() !== now.getDate() ||
    d.getMonth() !== now.getMonth() ||
    d.getFullYear() !== now.getFullYear()
  );
}

// Only resets group/community counters on the users doc (client-allowed).
// Veyl message counters (messagesUsedToday, voiceNotesUsedToday) are now
// reset server-side by the sendChatMessage / sendVoiceNote Cloud Functions.
async function resetDailyCounters(firebaseUser, data) {
  if (!data.isPremium && isFromPreviousDay(data.lastGroupReset)) {
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        groupMessagesUsedToday: 0,
        groupVoiceNotesUsedToday: 0,
        communityMessagesUsedToday: 0,
        lastGroupReset: serverTimestamp()
      });
      data = {
        ...data,
        groupMessagesUsedToday: 0,
        groupVoiceNotesUsedToday: 0,
        communityMessagesUsedToday: 0
      };
    } catch (e) {
      // Non-critical — counters will reset on next successful write
    }
  }
  return { data };
}

function determinePageFromVeyl(data, activeVeyl) {
  if (!data.onboardingComplete) return 'onboarding';
  if (activeVeyl) {
    const isMatched =
      (activeVeyl.status === 'matched' || activeVeyl.status === 'complete') &&
      activeVeyl.partnerId &&
      activeVeyl.partnerVeylId;
    if (isMatched) return 'dashboard';
  }
  return 'waiting';
}

function VeylLoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#080f1a',
    }}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 1; }
        }
        .loading-dots span {
          animation: pulse 1.2s ease-in-out infinite;
          display: inline-block;
        }
        .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
        .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
      <div style={{ animation: 'fadeInUp 0.8s ease both' }}>
        <svg
          width="160" height="64"
          viewBox="0 0 400 155"
          style={{ display: 'block', overflow: 'visible', marginBottom: '2rem' }}
        >
          <defs>
            <linearGradient id="textGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#008081" />
              <stop offset="55%"  stopColor="#256564" />
              <stop offset="100%" stopColor="#4B2C5E" />
            </linearGradient>
          </defs>
          <text
            x="200" y="140"
            textAnchor="middle"
            fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif"
            fontSize="155"
            fontWeight="800"
            letterSpacing="-4"
            fill="url(#textGrad)"
          >
            Veyl
          </text>
        </svg>
      </div>
      <div
        className="loading-dots"
        style={{
          display: 'flex',
          gap: '0.5rem',
          animation: 'fadeInUp 0.8s ease both',
          animationDelay: '0.3s'
        }}
      >
        {['#008081', '#256564', '#4B2C5E'].map((color, i) => (
          <span key={i} style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: color,
            animationDelay: `${i * 0.2}s`
          }} />
        ))}
      </div>
    </div>
  );
}

export default function MainApp() {
  const { setLanguage } = useLanguage();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('landing');
  const [showLanding, setShowLanding] = useState(true);
  const [theme, setTheme] = useState('dark');
  const prevPartnerIdRef = useRef(undefined);

  const handleSetTheme = async (newTheme) => {
    setTheme(newTheme);
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), { theme: newTheme });
      } catch (e) {
        // Theme save failed silently — non-critical
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

          // No user document at all — send to onboarding to create one
          if (!userDoc.exists()) {
            setCurrentPage('onboarding');
            setLoading(false);
            return;
          }

          let data = userDoc.data();

          if (data.theme) setTheme(data.theme);
          if (data.language) {
            setLanguage(data.language);
            localStorage.setItem('veyl_language', data.language);
          }

          if (data.accountDisabled) {
            setUserData(data);
            setCurrentPage('disabled');
            setLoading(false);
            return;
          }

          // Onboarding not complete — go straight there, no veyl needed
          if (!data.onboardingComplete) {
            setUserData(data);
            setCurrentPage('onboarding');
            setLoading(false);
            return;
          }

          let activeVeyl = await loadActiveVeyl(firebaseUser.uid);
          if (
            activeVeyl &&
            (activeVeyl.status === 'matched' || activeVeyl.status === 'complete') &&
            activeVeyl.partnerId &&
            !activeVeyl.partnerVeylId
          ) {
            await new Promise(res => setTimeout(res, 1500));
            activeVeyl = await loadActiveVeyl(firebaseUser.uid);
          }

          const reset = await resetDailyCounters(firebaseUser, data);
          data = reset.data;

          const combinedData = {
            ...data,
            activeVeyl: activeVeyl || null,
            activeVeylId: activeVeyl?.id || null,
            goalCategory: activeVeyl?.goalCategory || data.goalCategory || null,
            goalDescription: activeVeyl?.goalDescription || data.goalDescription || null,
            partnerId: activeVeyl?.partnerId || null,
            partnerVeylId: activeVeyl?.partnerVeylId || null,
            status: activeVeyl?.status || null,
            messagesUsedToday: activeVeyl?.messagesUsedToday || 0,
            voiceNotesUsedToday: activeVeyl?.voiceNotesUsedToday || 0,
            groupMessagesUsedToday: data.groupMessagesUsedToday || 0,
            groupVoiceNotesUsedToday: data.groupVoiceNotesUsedToday || 0,
            communityMessagesUsedToday: data.communityMessagesUsedToday || 0,
            ageGroup: data.ageGroup || null,
            timezone: data.timezone || activeVeyl?.timezone || null,
            matchPreference: data.matchPreference || activeVeyl?.matchPreference || 'nearby',
            name: data.name || null,
            blockedUsers: data.blockedUsers || [],
            avatarId: data.avatarId || null,
            isPremium: data.isPremium || false,
            communityId: data.communityId || null,
            groupId: data.groupId || null,
            language: data.language || null,
          };

          prevPartnerIdRef.current = activeVeyl?.partnerId ?? null;
          setUserData(combinedData);
          setCurrentPage(determinePageFromVeyl(data, activeVeyl));
        } catch (err) {
          console.error('MainApp auth load error:', err);
          // On any unexpected error, default to onboarding if user exists
          // rather than leaving them on a broken dashboard
          setCurrentPage('onboarding');
        }
      } else {
        setUser(null);
        setUserData(null);
        setCurrentPage('landing');
        setShowLanding(true);
        setTheme('dark');
        prevPartnerIdRef.current = undefined;
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.accountDisabled) {
        setCurrentPage('disabled');
        return;
      }
      setUserData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          communityId:                data.communityId                ?? null,
          groupId:                    data.groupId                    ?? null,
          isPremium:                  data.isPremium                  ?? false,
          mutedNotifications:         data.mutedNotifications         ?? [],
          groupMessagesUsedToday:     data.groupMessagesUsedToday     ?? 0,
          groupVoiceNotesUsedToday:   data.groupVoiceNotesUsedToday   ?? 0,
          communityMessagesUsedToday: data.communityMessagesUsedToday ?? 0,
          avatarId:                   data.avatarId                   ?? prev.avatarId,
          name:                       data.name                       ?? prev.name,
          language:                   data.language                   ?? prev.language,
          theme:                      data.theme                      ?? prev.theme,
          goalsAchieved:              data.goalsAchieved              ?? 0,
          ageGroup:                   data.ageGroup                   ?? prev.ageGroup,
          timezone:                   data.timezone                   ?? prev.timezone,
          matchPreference:            data.matchPreference            ?? prev.matchPreference,
          blockedUsers:               data.blockedUsers               ?? prev.blockedUsers,
        };
      });
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user || !userData?.activeVeylId) return;
    const unsubscribe = onSnapshot(doc(db, 'veyls', userData.activeVeylId), (snap) => {
      if (!snap.exists()) return;
      const veylData = { id: snap.id, ...snap.data() };
      setUserData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          activeVeyl:          veylData,
          partnerId:           veylData.partnerId        ?? null,
          partnerVeylId:       veylData.partnerVeylId    ?? null,
          status:              veylData.status           ?? null,
          goalCategory:        veylData.goalCategory     ?? prev.goalCategory,
          goalDescription:     veylData.goalDescription  ?? prev.goalDescription,
          messagesUsedToday:   veylData.messagesUsedToday   ?? 0,
          voiceNotesUsedToday: veylData.voiceNotesUsedToday ?? 0,
          timezone:            veylData.timezone            ?? prev.timezone,
          matchPreference:     veylData.matchPreference     ?? prev.matchPreference,
        };
      });

      const isMatched =
        (veylData.status === 'matched' || veylData.status === 'complete') &&
        veylData.partnerId &&
        veylData.partnerVeylId;

      // Only redirect on an actual matched→unmatched transition, not on
      // every unrelated write to the veyl doc (e.g. Rasa's message
      // counter, or a goal edit) while already unmatched. Comparing
      // against a ref of the previous partnerId — rather than just
      // checking the current snapshot — avoids re-triggering the
      // redirect on writes that don't change match state at all.
      const previousPartnerId = prevPartnerIdRef.current;
      const justLostPartner = previousPartnerId != null && !veylData.partnerId;
      prevPartnerIdRef.current = veylData.partnerId ?? null;

      setCurrentPage(prev => {
        if (isMatched && prev === 'waiting') return 'dashboard';
        if (justLostPartner && prev === 'dashboard') return 'waiting';
        return prev;
      });
    });
    return () => unsubscribe();
  }, [user, userData?.activeVeylId]);

  useEffect(() => {
    if (currentPage !== 'waiting' || !user) return;
    const interval = setInterval(async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) return;
        let data = userDoc.data();
        if (data.accountDisabled) {
          setUserData(data);
          setCurrentPage('disabled');
          clearInterval(interval);
          return;
        }
        let activeVeyl = await loadActiveVeyl(user.uid);
        const reset = await resetDailyCounters({ uid: user.uid }, data);
        data = reset.data;
        if (
          activeVeyl &&
          (activeVeyl.status === 'matched' || activeVeyl.status === 'complete') &&
          activeVeyl.partnerId &&
          activeVeyl.partnerVeylId
        ) {
          prevPartnerIdRef.current = activeVeyl.partnerId;
          setUserData(prev => ({
            ...prev,
            activeVeyl,
            activeVeylId:               activeVeyl.id,
            partnerId:                  activeVeyl.partnerId,
            partnerVeylId:              activeVeyl.partnerVeylId,
            status:                     activeVeyl.status,
            goalCategory:               activeVeyl.goalCategory,
            goalDescription:            activeVeyl.goalDescription,
            messagesUsedToday:          activeVeyl.messagesUsedToday          || 0,
            voiceNotesUsedToday:        activeVeyl.voiceNotesUsedToday        || 0,
            groupMessagesUsedToday:     data.groupMessagesUsedToday           || 0,
            groupVoiceNotesUsedToday:   data.groupVoiceNotesUsedToday         || 0,
            communityMessagesUsedToday: data.communityMessagesUsedToday       || 0,
            communityId:                data.communityId                      ?? null,
          }));
          setCurrentPage('dashboard');
        }
      } catch (err) {
        // Waiting page poll failed silently — will retry on next interval
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [currentPage, user]);

  if (loading) return <VeylLoadingScreen />;
  if (!user) {
    if (currentPage === 'landing' && showLanding) {
      return <LandingPage onGetStarted={() => setShowLanding(false)} />;
    }
    return (
      <AuthPage onShowLanding={() => {
        setShowLanding(true);
        setCurrentPage('landing');
      }} />
    );
  }
  if (currentPage === 'disabled') return <DisabledPage />;
  if (currentPage === 'onboarding') return (
    <OnboardingPage
      user={user}
      setCurrentPage={setCurrentPage}
      setUserData={setUserData}
    />
  );
  if (currentPage === 'waiting') return (
    <WaitingPage
      userData={userData}
      user={user}
      setUserData={setUserData}
      setCurrentPage={setCurrentPage}
    />
  );
  return (
    <DashboardPage
      user={user}
      userData={userData}
      setUserData={setUserData}
      theme={theme}
      setTheme={handleSetTheme}
    />
  );
}